<template>
  <TabGroup
    v-if="tabs.length !== 0"
    :selected-index="selectedTab"
    @change="changeTab"
  >
    <TabList class="border-b border-gray-300">
      <Tab
        v-for="tab in tabs"
        v-slot="{ selected }"
        :key="tab"
        as="template"
      >
        <button
          class="wc-focus body-md whitespace-nowrap border-b-4 px-4 py-3 text-center text-gray-800"
          :class="{
            'border-primary-500 font-semibold': selected,
            'border-transparent  hover:border-gray-300': !selected
          }"
          v-text="tab"
        />
      </Tab>
    </TabList>
    <TabPanels class="pt-6">
      <slot />
    </TabPanels>
  </TabGroup>
</template>

<script setup lang="ts">
import { TabGroup, TabList, Tab, TabPanels } from '@headlessui/vue'
import { onBeforeMount, ref } from 'vue'

import { slugify } from '@js/utils/strings'

defineOptions({
  name: 'AppTabs',
})

const props = withDefaults(
  defineProps<{
    tabs: string[]
    /** Search state keys to drop on a tab change, so one tab's search does not follow the user to the next */
    clearParams?: string[]
  }>(),
  {
    clearParams: () => [],
  },
)

const selectedTab = ref(0)

// A search namespaces its params by state key, e.g. `live[query]`
const isClearable = (param: string) => props.clearParams
  .some(key => param === key || param.startsWith(`${key}[`))

const changeTab = (index: number) => {
  const url = new URL(window.location.href)

  Array.from(url.searchParams.keys())
    .filter(isClearable)
    .forEach(param => url.searchParams.delete(param))

  url.hash = slugify(props.tabs[index])

  window.history.pushState(null, '', url.toString())
  selectedTab.value = index
}

onBeforeMount( () => {
  selectedTab.value = props.tabs.findIndex(element => slugify(element) === window.location.hash.replace('#', ''))
})
</script>
